import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { MetaTags } from '../../components/Metatags/Metatags';
import { BRAND_LUXE_PRIMARY } from './brandDashboardTokens';
import {
    fetchCampaignHostMine,
    fetchCampaignHostPricing,
    updateCampaignHostPaymentMethod,
} from '../../redux/campaignHostSubscription/actions';
import {
    campaignHostLtdPromoActiveSelector,
    campaignHostPackagesSelector,
    campaignHostPricingSelector,
    campaignHostSubscriptionLoadingSelector,
    campaignHostSubscriptionSelector,
    hasActiveCampaignHostPlanSelector,
} from '../../redux/campaignHostSubscription/selectors';
import { CampaignHostTierPickerModal } from './subscription/CampaignHostTierPickerModal';
import { CampaignHostCheckoutDialog } from './subscription/CampaignHostCheckoutDialog';
import { CampaignHostSubscriptionEmptyView } from './subscription/CampaignHostSubscriptionEmptyView';
import { CampaignHostSubscriptionActiveView } from './subscription/CampaignHostSubscriptionActiveView';
import { CampaignHostLtdPromoModal } from './subscription/CampaignHostLtdPromoModal';
import {
    dismissCampaignHostLtdPromo,
    isCampaignHostLtdPromoDismissed,
} from './subscription/campaignHostLtdPromoDismiss';
import { useCampaignHostCheckout } from './subscription/useCampaignHostCheckout';
import type { ICampaignHostPackage } from '../../types/global/campaignHostSubscription.types';
import { CampaignHostPaystackRedirectOverlay } from './subscription/CampaignHostPaystackRedirectOverlay';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';

/** Subscription settings only — Meta ad account lives on Social integration / boost modal. */
export default function BrandSubscriptionSettingsPage() {
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const [tierModalOpen, setTierModalOpen] = useState(false);
    const [ltdPromoModalOpen, setLtdPromoModalOpen] = useState(false);

    const loading = useSelector(campaignHostSubscriptionLoadingSelector);
    const hasPlan = useSelector(hasActiveCampaignHostPlanSelector);
    const subscription = useSelector(campaignHostSubscriptionSelector);
    const pricing = useSelector(campaignHostPricingSelector);
    const packages = useSelector(campaignHostPackagesSelector);
    const ltdPromoActive = useSelector(campaignHostLtdPromoActiveSelector);

    const closeTierModal = useCallback(() => setTierModalOpen(false), []);

    const {
        checkoutOpen,
        setCheckoutOpen,
        beginMonthly,
        beginLtd,
        beginUpdatePaymentMethod,
        onPaymentMethodReady,
        checkoutTitle,
        paystackRedirecting,
        redirectProvider,
    } = useCampaignHostCheckout({ onSubscriptionComplete: closeTierModal });

    useEffect(() => {
        dispatch(fetchCampaignHostPricing());
        dispatch(fetchCampaignHostMine());
    }, [dispatch]);

    useEffect(() => {
        if (loading || hasPlan) {
            setLtdPromoModalOpen(false);
            return;
        }
        if (ltdPromoActive && !isCampaignHostLtdPromoDismissed()) {
            setLtdPromoModalOpen(true);
        }
    }, [loading, hasPlan, ltdPromoActive]);

    /** Loads tiers from GET /campaign-host-subscriptions/pricing (+ packages fallback in saga). */
    const openTierModal = useCallback(() => {
        setTierModalOpen(true);
        dispatch(fetchCampaignHostPricing());
    }, [dispatch]);

    const handleSelectMonthly = useCallback(
        (pkg: ICampaignHostPackage) => {
            closeTierModal();
            beginMonthly(pkg);
        },
        [closeTierModal, beginMonthly],
    );

    const handleSelectLtd = useCallback(
        (pkg: ICampaignHostPackage) => {
            closeTierModal();
            beginLtd(pkg);
        },
        [closeTierModal, beginLtd],
    );

    const closeLtdPromoModal = useCallback(() => {
        dismissCampaignHostLtdPromo();
        setLtdPromoModalOpen(false);
    }, []);

    const handlePromoClaim = useCallback(
        (pkg: ICampaignHostPackage) => {
            setLtdPromoModalOpen(false);
            beginLtd(pkg);
        },
        [beginLtd],
    );

    const handleViewAllPlansFromPromo = useCallback(() => {
        closeLtdPromoModal();
        openTierModal();
    }, [closeLtdPromoModal, openTierModal]);

    const handleManageBilling = useCallback(async () => {
        if (!subscription) return;
        if (subscription.platform === 'paystack') {
            let url = subscription.paystack_details?.manage_payment_url;
            if (!url && subscription._id) {
                try {
                    const result = (await dispatchPromise(
                        updateCampaignHostPaymentMethod({ subscriptionId: subscription._id }),
                    )) as { manage_payment_url?: string };
                    url = result?.manage_payment_url;
                } catch {
                    showToastMessage('Could not open Paystack billing page.', 'error');
                    return;
                }
            }
            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer');
                return;
            }
            showToastMessage('Manage billing is not available for this subscription.', 'info');
            return;
        }
        if (subscription._id) beginUpdatePaymentMethod(subscription._id);
    }, [subscription, beginUpdatePaymentMethod, dispatchPromise]);

    const promoPriceCents = pricing?.ltd_promotion?.promo_price_cents ?? null;

    return (
        <>
            <MetaTags title="Settings | Thspian" description="Manage your campaign host subscription">
                <CampaignHostPaystackRedirectOverlay open={paystackRedirecting} provider={redirectProvider ?? 'paystack'} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 4 }}>
                    {loading && !subscription && !pricing ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
                            <CircularProgress sx={{ color: BRAND_LUXE_PRIMARY }} />
                        </Box>
                    ) : hasPlan && subscription ? (
                        <CampaignHostSubscriptionActiveView
                            subscription={subscription}
                            onManageBilling={handleManageBilling}
                        />
                    ) : (
                        <CampaignHostSubscriptionEmptyView onExplorePlans={openTierModal} />
                    )}
                </Box>
            </MetaTags>

            <CampaignHostLtdPromoModal
                open={ltdPromoModalOpen && ltdPromoActive}
                onClose={closeLtdPromoModal}
                packages={packages}
                promoPriceCents={promoPriceCents}
                promoEndsAt={pricing?.ltd_promotion?.ends_at ?? null}
                ltdPromoActive={ltdPromoActive}
                onClaimLtd={handlePromoClaim}
                onViewAllPlans={handleViewAllPlansFromPromo}
            />

            <CampaignHostTierPickerModal
                open={tierModalOpen}
                onClose={closeTierModal}
                onSelectMonthly={handleSelectMonthly}
                onSelectLtd={handleSelectLtd}
            />

            <CampaignHostCheckoutDialog
                open={checkoutOpen}
                title={checkoutTitle}
                onClose={() => setCheckoutOpen(false)}
                onPaymentMethodReady={onPaymentMethodReady}
            />
        </>
    );
}
